import {
  Briefcase,
  PartyPopper,
  Users,
  Zap,
  CheckCircle,
  CalendarClock,
  AlertTriangle,
  Image,
  Award,
  ShieldAlert,
} from 'lucide-react';
import type { EventTeamStats } from '../../types/eventTeam';
import { StatTile } from '../ui/StatTile';

interface EventStatsCardsProps {
  stats: EventTeamStats | null;
}

type Tone = 'neutral' | 'accent' | 'purple' | 'success' | 'warning' | 'amber' | 'orange' | 'danger' | 'info';

/** Headline dashboard stats from `GET /events/projects/stats` (KV-cached). */
export const EventStatsCards = ({ stats }: EventStatsCardsProps) => {
  if (!stats) return null;

  const eventsInProgress = stats.events_by_status?.in_progress ?? 0;
  const projectsInProgress = stats.projects_by_status?.in_progress ?? 0;
  const inProgressTotal = eventsInProgress + projectsInProgress;

  const eventsCompleted = stats.events_by_status?.completed ?? 0;
  const projectsCompleted = stats.projects_by_status?.completed ?? 0;
  const completedTotal = eventsCompleted + projectsCompleted;

  const ideas = stats.events_by_status?.idea ?? 0;

  const cards: { label: string; value: number; icon: typeof PartyPopper; tone: Tone }[] = [
    { label: 'Total Events', value: stats.total_events, icon: PartyPopper, tone: 'accent' },
    { label: 'Projects', value: stats.total_projects, icon: Briefcase, tone: 'info' },
    { label: 'Active Members', value: stats.team_members_active, icon: Users, tone: 'purple' },
    { label: 'Ideas', value: ideas, icon: Zap, tone: 'neutral' },
    { label: 'In Progress', value: inProgressTotal, icon: Zap, tone: 'amber' },
    { label: 'Completed', value: completedTotal, icon: CheckCircle, tone: 'success' },
    { label: 'Upcoming (7d)', value: stats.upcoming_events_7d, icon: CalendarClock, tone: 'accent' },
    {
      label: 'Overdue Tasks',
      value: stats.overdue_tasks,
      icon: AlertTriangle,
      tone: stats.overdue_tasks > 0 ? 'danger' : 'neutral',
    },
    { label: 'Media', value: stats.total_media, icon: Image, tone: 'info' },
    { label: 'Points Awarded', value: stats.points_awarded_total, icon: Award, tone: 'warning' },
    {
      label: 'Flagged Teams',
      value: stats.checks_flagged_teams,
      icon: ShieldAlert,
      tone: stats.checks_flagged_teams > 0 ? 'amber' : 'neutral',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
      {cards.map((card) => (
        <StatTile
          key={card.label}
          label={card.label}
          value={(card.value ?? 0).toLocaleString()}
          icon={card.icon}
          tone={card.tone}
        />
      ))}
    </div>
  );
};
